import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormikProps } from "formik";
import { AddButton } from "../Buttons";
import { Currency, formatFloat } from "../../utils";
import { FormSelect, FormTextInput } from "../FormFields";
import { PencilSquareIcon } from "@heroicons/react/20/solid";
import {
  PaymentInterface,
  PaymentMethodEnum,
  PaymentMethodInterface,
  PaymentStatusEnum,
} from "../../interfaces";
import { useAppSelector } from "../../store/hooks";
import { loaderService } from "../../services";

export interface PercentageInterface {
  id: number;
  name: string;
  value: string;
  percentage: number;
  initialPercentage: number;
}

export interface RetentionFormValues {
  amount: number;
  retentionPercent: number;
  paymentMethod: PaymentMethodInterface;
  currency: Currency;
}

interface RetentionFormProps {
  iva: number;
  subTotal: number;
  remaining: number;
  formik: FormikProps<RetentionFormValues>;
  payments: PaymentInterface[];
  defaultPercentages: PercentageInterface[];
  onEmptyOptions?: () => void;
  handlePayAmountChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    formik: FormikProps<any>,
    max: number
  ) => void;
}

export const retentionList = [
  "iva-retention",
  "islr-retention",
  "municiapl-tax",
];

const RetentionsForm: FC<RetentionFormProps> = ({
  iva,
  subTotal,
  remaining,
  formik,
  payments,
  defaultPercentages,
  onEmptyOptions = () => {},
  handlePayAmountChange,
}) => {
  const loading = loaderService.useIsLoading();
  const [isValueEditable, setIsValueEditable] = useState(false);
  const [isAddPercentageOption, setIsAddPercentageOption] = useState(false);
  const initialRetentionMethods = useAppSelector(
    (state) => state.user.retentionMethods ?? []
  );
  const paymentMethodID = formik.values.paymentMethod?.paymentMethodID;

  const retentionOptions = useMemo(() => {
    const activePayments =
      payments?.filter(
        (p) =>
          p.status === PaymentStatusEnum.PENDING ||
          p.status === PaymentStatusEnum.APPROVE
      ) ?? [];

    const isrlCount = activePayments.filter(
      (p) =>
        p.paymentMethod.paymentMethodID === PaymentMethodEnum.RETENCION_DE_ISRL
    ).length;

    const municipalCount = activePayments.filter(
      (p) =>
        p.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.RETENCION_DE_IMPUESTO_MUNICIPAL
    ).length;

    const options = initialRetentionMethods.filter((option) => {
      if (
        option.paymentMethodID === PaymentMethodEnum.RETENCION_DE_ISRL &&
        isrlCount >= 2
      ) {
        return false;
      }

      if (
        option.paymentMethodID ===
          PaymentMethodEnum.RETENCION_DE_IMPUESTO_MUNICIPAL &&
        municipalCount >= 2
      ) {
        return false;
      }

      return (
        (option.paymentMethodID === PaymentMethodEnum.RETENCION_DE_ISRL ||
          option.paymentMethodID ===
            PaymentMethodEnum.RETENCION_DE_IMPUESTO_MUNICIPAL ||
          !activePayments.some(
            (payment) =>
              payment.paymentMethod.paymentMethodName ===
              option.paymentMethodName
          )) &&
        (iva > 0 ||
          option.paymentMethodID !== PaymentMethodEnum.RETENCION_DE_IVA)
      );
    });

    return options;
    // To update the retention options you only need to know the payments,
    // not the formik object. Adding it would cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, iva, initialRetentionMethods]);

  useEffect(() => {
    if (retentionOptions.length === 0) {
      if (paymentMethodID) {
        formik.setFieldValue("paymentMethod", {} as PaymentMethodInterface);
      }

      return;
    }

    const selectedOption =
      retentionOptions.find(
        (option) => option.paymentMethodID === paymentMethodID
      ) ?? retentionOptions[0];

    if (selectedOption.paymentMethodID !== paymentMethodID) {
      formik.setFieldValue("paymentMethod", selectedOption);
    }
  }, [formik, paymentMethodID, retentionOptions]);

  const clamp = useCallback(
    (value: number) => Math.max(0, Math.min(value, remaining)),
    [remaining]
  );

  const handlePercentageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    formik: FormikProps<RetentionFormValues>
  ) => {
    const setValue = (value: string) => {
      e.target.value = value;
    };
    if (!formatFloat(e.target.value, setValue)) {
      return;
    }

    formik.setValues({
      ...formik.values,
      amount: +clamp((subTotal * Number(e.target.value)) / 100).toFixed(2),
      retentionPercent: e.target.value as unknown as number,
    });
  };

  useEffect(() => {
    setIsValueEditable(
      paymentMethodID ===
        PaymentMethodEnum.RETENCION_DE_ISRL
    );
  }, [paymentMethodID]);

  useEffect(() => {
    setIsAddPercentageOption(
      paymentMethodID ===
        PaymentMethodEnum.RETENCION_DE_IMPUESTO_MUNICIPAL
    );
  }, [paymentMethodID]);

  useEffect(() => {
    const percentageObj = defaultPercentages.find(
      (option) => option.id === paymentMethodID
    );

    if (
      paymentMethodID ===
      PaymentMethodEnum.RETENCION_DE_IVA
    ) {
      formik.setFieldValue("amount", +(iva * 0.75).toFixed(2));
    } else if (percentageObj) {
      formik.setFieldValue(
        "amount",
        Number(clamp(subTotal * percentageObj.initialPercentage).toFixed(2))
      );
    } else if (
      paymentMethodID ===
      PaymentMethodEnum.RETENCION_DE_IMPUESTO_MUNICIPAL
    ) {
      formik.setValues({
        ...formik.values,
        retentionPercent: 3,
        amount: Number(clamp((subTotal * 3) / 100).toFixed(2)),
      });
      setIsAddPercentageOption(true);
    } else {
      setIsAddPercentageOption(false);
    }

    // Adding the formik object would cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    iva,
    defaultPercentages,
    formik.values.paymentMethod?.paymentMethodName,
    subTotal,
    clamp,
    paymentMethodID,
  ]);

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="flex items-end gap-4 w-full"
    >
      <div className="flex items-start gap-4 w-full">
        <div className="flex flex-col flex-1">
          <FormSelect
            label="Tipo de Retención"
            name="retentionType"
            options={retentionOptions}
            optionString={(method) => `${method.paymentMethodName}`}
            disabled={retentionOptions.length === 0}
            selected={
              retentionOptions.length === 0
                ? ({
                    paymentMethodID: 0,
                    paymentMethodName:
                      "No hay retenciones disponibles para agregar",
                  } as PaymentMethodInterface)
                : retentionOptions.find(
                    (m) =>
                      m.paymentMethodID ===
                      formik.values.paymentMethod?.paymentMethodID
                  ) ??
                  ({
                    paymentMethodID: 0,
                    paymentMethodName: "Buscar...",
                  } as PaymentMethodInterface)
            }
            error={
              formik.touched.paymentMethod && formik.errors.paymentMethod
                ? formik.errors.paymentMethod
                : ""
            }
            onSelectOption={(method) =>
              formik.setFieldValue("paymentMethod", method)
            }
            onBlur={formik.handleBlur}
          />
        </div>

        {retentionOptions.length !== 0 && (
          <div className="flex flex-1 space-x-2">
            {isAddPercentageOption && (
              <div className="flex-1">
                <FormTextInput
                  name="retentionPercent"
                  label="%"
                  onChange={(e) => handlePercentageChange(e, formik)}
                  value={formik.values.retentionPercent}
                  error={
                    formik.touched.retentionPercent &&
                    formik.errors.retentionPercent
                      ? formik.errors.retentionPercent
                      : ""
                  }
                  onBlur={formik.handleBlur}
                />
              </div>
            )}
            <div className="flex-1">
              <FormTextInput
                name="amount"
                label="Monto (Bs)"
                readOnly={!isValueEditable}
                onChange={(e) => handlePayAmountChange(e, formik, remaining)}
                value={formik.values.amount}
                error={formik.errors.amount}
                onBlur={formik.handleBlur}
              />
            </div>
          </div>
        )}
      </div>

      {!isValueEditable && retentionOptions.length !== 0 && (
        <button
          type="submit"
          title="Editar decimales"
          data-te-toggle="tooltip"
          onClick={() => setIsValueEditable(true)}
          className="flex flex-row justify-center text-sm font-semibold items-center rounded-full 
        bg-indigo-100 hover:bg-indigo-200 hover:cursor-pointer px-3 py-2 "
        >
          <PencilSquareIcon
            className="h-5 w-5 text-indigo-600"
            aria-hidden="true"
          />
          Ajustar
        </button>
      )}

      <div>
        <AddButton
          type="submit"
          disabled={
            !formik.values.amount || loading || retentionOptions.length === 0
          }
        />
      </div>
    </form>
  );
};

export default RetentionsForm;
